export type Locale = 'pt' | 'en' | 'fr' | 'es'

export type CatalogItem = {
  id: string
  title: string
  description: string
  price: string
  tag: string
}

export type NewsItem = {
  id: string
  title: string
  excerpt: string
  date: string
  badge: string
}

export type NavItem = {
  id: string
  label: string
}

export type Translation = {
  localeLabel: string
  nav: NavItem[]
  header: {
    brand: string
    tagline: string
  }
  ui: {
    cart: string
    menu: string
    toggleMenu: string
    language: string
    close: string
    home: string
  }
  hero: {
    kicker: string
    title: string
    description: string
    primaryCta: string
    secondaryCta: string
    stats: { value: string; label: string; img?: string }[]
  }
  hero2: {
    title: string
    description: string
    primaryCta: string
  }
  catalog: {
    title: string
    description: string
    cta: string
    loading: string
    loadError: string
    badges: {
      bestseller: string
      classic: string
    }
    fallbackCategory: string
    items: CatalogItem[]
  }
  heroSwiper: {
    regionLabel: string
    imageAlt: string
    overlayTitle: string
    overlayCta: string
    goToSlide: string
  }
  whoWeAre: {
    kicker: string
    title: string
    intro: string
    essentialsIntro: string
    essentials: { title: string; body: string }[]
    closing: string
  }
  story: {
    title: string
    subtitle: string
    paragraphs: string[]
    processTitle: string
    processSteps: string[]
    processNote: string
  }
  news: {
    title: string
    description: string
    button: string
    posts: NewsItem[]
  }
  shop: {
    title: string
    description: string
    bullets: string[]
    badge: string
    cta: string
    logisticsTitle: string
    logisticsDescription: string
    logisticsFeatures: string[]
  }
  contact: {
    title: string
    description: string
    form: {
      name: string
      email: string
      message: string
      send: string
    }
    info: {
      address: string
      schedule: string
      phone: string
      email: string
    }
    labels: {
      atelier: string
      schedule: string
      phone: string
      email: string
    }
    note: string
  }
  footer: {
    rights: string
    tagline: string
  }
  cartPage: {
    title: string
    empty: string
    browseCatalog: string
    qty: string
    remove: string
    orderSummary: string
    total: string
    proceedToCheckout: string
  }
  checkout: {
    title: string
    emptyCartAlert: string
    fullName: string
    email: string
    addressLine1: string
    addressLine2: string
    city: string
    postalCode: string
    cardNumber: string
    cardExpiry: string
    cardCvc: string
    placingOrder: string
    placeOrder: string
    thankYou: string
    failed: string
    requiredFields: string
    canceled: string
  }
  product: {
    loading: string
    missingId: string
    notFound: string
    tryCatalog: string
    addToCart: string
    saveForLater: string
    ingredients: string
    ingredientsList: string[]
    packagingNote: string
  }
  apiErrors: {
    request_failed: string
    bad_request: string
    invalid_json: string
    unsupported_media_type: string
    payload_too_large: string
    not_found: string
    rate_limited: string
    server_error: string
    validation_error: string
  }
  landing: {
    nav: {
      historia: string
      encomendas: string
      processo: string
      envios: string
      faq: string
      encomendar: string
    }
    heroTitle: string
    heroTitleEm: string
    heroDesc: string
    heroBadge: { title: string; text: string }
    strip: string[]
    animSection: {
      badge: string
      title: string
      titleEm: string
      body: string
    }
    historia: {
      kicker: string
      title: string
      body: string[]
      caption: string
      captionSource: string
      year: string
    }
    destSelector: {
      title: string
      PT: { label: string; banner: string }
      'PT-I': { label: string; banner: string }
      ES: { label: string; banner: string }
      EU: { label: string; banner: string }
      BR: { label: string; banner: string }
    }
    products: {
      sectionLabel: string
      title: string
      subtitle: string
      highlight: string
    }
    lifestyle: {
      caption1: string
      caption2: string
    }
    shipping: {
      sectionLabel: string
      title: string
      titleEm: string
      subTitle: string
      subtitle: string
      th: { dest: string; time: string; halfDozen: string; dozen: string; giftBox: string }
      rows: { flag: string; dest: string; time: string; halfDozen: string; dozen: string; giftBox: string }[]
      footnote: string
      infoTitle: string
      infoItems: string[]
    }
    processo: {
      sectionLabel: string
      title: string
      steps: { title: string; body: string }[]
      transparencia: { label: string; body: string }
    }
    testemunhos: {
      sectionLabel: string
      title: string
      items: { text: string; author: string }[]
    }
    faq: {
      sectionLabel: string
      title: string
      items: { q: string; a: string }[]
    }
    cta: {
      sectionLabel: string
      title: string
      titleEm: string
      body: string
      button: string
    }
    footer: {
      col1Title: string
      col1Links: { label: string; anchor: string }[]
      col2Title: string
      col2Links: { label: string; href: string }[]
      bottomTagline: string
    }
  }
}

export const translations: Record<Locale, Translation> = {
  pt: {
    localeLabel: 'Português',
    nav: [
      { id: 'catalog', label: 'Catálogo' },
      { id: 'taste', label: 'Quero Provar' },
      { id: 'why-caladinhos', label: 'Porquê Caladinhos' },
      { id: 'who-we-are', label: 'Quem Somos' },
      { id: 'story', label: 'Quem somos' },
      { id: 'news', label: 'Novidades' },
      { id: 'shop', label: 'Loja Online' },
      { id: 'contact', label: 'Contactos' },
    ],
    header: {
      brand: 'Caladinhos',
      tagline: 'Bolinhos artesanais de Santa Maria da Feira, entregues em Portugal e no mundo. Porque há sabores que são saudade.',
    },
    ui: {
      cart: 'Carrinho',
      menu: 'Menu',
      toggleMenu: 'Alternar menu',
      language: 'Idioma',
      close: 'Fechar',
      home: 'Início',
    },
    hero: {
      kicker: 'Caladinhos & amigos',
      title: 'O silêncio nunca foi tão delicioso',
      description:
        'Descubra o segredo mais bem guardado de Santa Maria da Feira. Biscoitos artesanais, feitos com amor e tradição, entregues à sua porta',
      primaryCta: 'Explorar catálogo',
      secondaryCta: 'Novidades',
      stats: [
        { value: '120+', label: 'Receitas sazonais', img: '/apps/ca/img/decor/bread_transparent_04.png' },
        { value: '36h', label: 'Do forno à porta', img: '/apps/ca/img/decor/bread_transparent_05.png' },
        { value: '4.9★', label: 'Avaliação dos clientes', img: '/apps/ca/img/decor/bread_transparent_06.png' },
      ],
    },
    hero2: {
      title: 'A Lenda Nascida num Sussurro',
      description:
        'Em 1934, para proteger reuniões secretas, o padeiro pedia silêncio: “Shiu... estamos a fazer os caladinhos”. O silêncio que antes era proteção, hoje é a melhor forma de partilhar e saborear este momento único.',
      primaryCta: 'Quem Somos',
    },
    catalog: {
      title: 'Momentos de silêncio para saborear e partilhar',
      description:
        'Clássicos intemporais e criações exclusivas, feitos em pequenos lotes e embalados para viagens seguras.',
      cta: 'Pré-encomendar',
      loading: 'A carregar produtos…',
      loadError: 'Não foi possível carregar o catálogo',
      badges: {
        bestseller: 'Mais vendido',
        classic: 'Clássico',
      },
      fallbackCategory: 'Catálogo',
      items: [
        {
          id: 'classic',
          title: 'Caladinho Clássico',
          description: 'Massa fofa amanteigada, toque cítrico e cobertura cristalizada.',
          price: '18€ / caixa',
          tag: 'mais pedido',
        },
        {
          id: 'almond',
          title: 'Fogaça de Amêndoa',
          description: 'Farinha de espelta e praliné crocante, inspirada nas feiras de Santa Maria.',
          price: '22€ / caixa',
          tag: 'edição limitada',
        },
        {
          id: 'citrus',
          title: 'Travesseiro Citrino',
          description: 'Folhado leve com compota de laranja amarga e baunilha de São Jorge.',
          price: '19€ / 6 unid.',
          tag: 'novo',
        },
        {
          id: 'vegan',
          title: 'Caladinho Vegan',
          description: 'Leite de aveia, açúcar mascavado e azeite DOP para um perfil mais leve.',
          price: '20€ / caixa',
          tag: 'plant based',
        },
      ],
    },
    heroSwiper: {
      regionLabel: 'Imagens destaque da página inicial',
      imageAlt: 'Imagem destaque Caladinhos',
      overlayTitle: 'Raízes com sabor de sempre',
      overlayCta: 'Saber mais',
      goToSlide: 'Ir para o slide {n}',
    },
    whoWeAre: {
      kicker: 'Quem Somos',
      title: 'Três Gerações, uma Receita - O segredo que o tempo não calou.',
      intro:
        'Em 1934, numa pequena cozinha em Santa Maria da Feira, nascia mais do que um biscoito: nascia um símbolo de união e resistência. Reza a lenda que o nome surgiu de um sussurro — "Shiu... caladinhos" — para proteger os encontros de quem sonhava com tempos mais livres.',
      essentialsIntro:
        'Três gerações depois, continuamos a honrar esse silêncio respeitoso perante a tradição. Não abdicamos do essencial:',
      essentials: [
        {
          title: 'O Tempo',
          body: 'Respeitamos o repouso da massa e o calor lento do forno para garantir a textura perfeita.',
        },
        {
          title: 'A Origem',
          body: 'Selecionamos apenas ingredientes locais, desde a manteiga pura até aos ovos de galinhas criadas em campo aberto.',
        },
        {
          title: 'O Toque Manual',
          body: 'Cada caladinho é de origem de Santa Maria da Feira e passa pelas nossas mãos antes de chegar à sua mesa, mantendo vivo o controlo e o saber original em cada etapa.',
        },
      ],
      closing:
        'Hoje, o silêncio que outrora era segredo, é agora um convite. Um convite para parar, partilhar e saborear o que é genuíno.',
    },
    story: {
      title: 'Raízes artesanais, visão contemporânea',
      subtitle: 'De Santa Maria da Feira para o mundo digital',
      paragraphs: [
        'Caladinhos nasceu em 1963 numa cozinha familiar em Santa Maria da Feira. Três gerações depois continuamos a usar cestos de fermentação em linho, manteiga local e ovos de galinhas em campo aberto.',
        'A nossa loja online permite encomendas personalizadas, experiências de prova híbridas e serviços corporativos, mantendo o controlo manual em cada etapa da produção.',
      ],
      processTitle: 'Processo',
      processSteps: [
        'Seleção de ingredientes locais certificados',
        'Fermentações longas para textura aerada',
        'Embalamento térmico e rastreado',
      ],
      processNote: '* Fluxo adaptável a cada encomenda',
    },
    news: {
      title: 'Novidades doces',
      description: 'Eventos, colaborações e lançamentos que ligam tradição e inovação.',
      button: 'Saber mais →',
      posts: [
        {
          id: 'market',
          title: 'Pop-up Caladinhos na LX Factory',
          excerpt: 'Degustação exclusiva com pairing de vinhos verdes e workshop sobre caldas aromáticas.',
          date: '12 Jan 2026',
          badge: 'evento',
        },
        {
          id: 'collab',
          title: 'Colaboração com mestres chocolateiros do Porto',
          excerpt: 'Novo Caladinho recheado com ganache de cacau de origem única e flor de sal.',
          date: '02 Jan 2026',
          badge: 'parceria',
        },
        {
          id: 'award',
          title: 'Prémio Great Taste para a edição Castanha',
          excerpt: 'Receita de outono premiada pela textura sedosa e equilíbrio entre especiarias.',
          date: '15 Dez 2025',
          badge: 'prémio',
        },
      ],
    },
    shop: {
      title: 'Loja online pronta a enviar',
      description:
        'Pré-encomendas semanais, embalagens térmicas e logística refrigerada garantem frescura máxima.',
      bullets: [
        'Envios expressos para Portugal e Espanha',
        'Checkout seguro, multibanco e MBWay',
        'Serviços corporativos e presentes personalizados',
      ],
      badge: 'Aberto 24/7',
      cta: 'Agendar prova digital',
      logisticsTitle: 'Logística premium',
      logisticsDescription:
        'Monitorizamos temperatura, humidade e tracking em tempo real para garantir experiências consistentes, seja para clientes finais ou gifting corporativo.',
      logisticsFeatures: [
        'Parceiros cold-chain',
        'Estúdio de packaging',
        'Tracking em tempo real',
        'Suporte 24h',
      ],
    },
    contact: {
      title: 'Contacte a nossa equipa',
      description: 'Respondemos em menos de 24h e adoramos desenhar experiências à medida.',
      form: {
        name: 'Nome completo',
        email: 'Email profissional',
        message: 'Mensagem / briefing',
        send: 'Enviar pedido',
      },
      info: {
        address: 'Atelier Caladinhos, Rua Castelo 42, Santa Maria da Feira',
        schedule: '2ª a 6ª, 08h00 - 18h00',
        phone: '+351 939 000 321',
        email: 'atelier@caladinhos.pt',
      },
      labels: {
        atelier: 'Atelier',
        schedule: 'Horário',
        phone: 'Telefone',
        email: 'Email',
      },
      note: 'Melhorar logística B2B? Criamos catálogos personalizados com lead time de 7 dias.',
    },
    footer: {
      rights: '© 2026 caladinhos.pt - Todos os direitos reservados.',
      tagline: 'Caladinhos • Pastelaria regional',
    },
    cartPage: {
      title: 'Carrinho',
      empty: 'O seu carrinho está vazio.',
      browseCatalog: 'Explorar catálogo',
      qty: 'Qtd',
      remove: 'Remover',
      orderSummary: 'Resumo da encomenda',
      total: 'Total',
      proceedToCheckout: 'Finalizar compra',
    },
    checkout: {
      title: 'Checkout',
      emptyCartAlert: 'O seu carrinho está vazio.',
      fullName: 'Nome completo',
      email: 'Email',
      addressLine1: 'Morada',
      addressLine2: 'Morada (linha 2)',
      city: 'Cidade',
      postalCode: 'Código Postal',
      cardNumber: 'Número do cartão',
      cardExpiry: 'MM/AA',
      cardCvc: 'CVC',
      placingOrder: 'A criar encomenda…',
      placeOrder: 'Fazer encomenda',
      thankYou: 'Obrigado! Encomenda #{orderId} criada.',
      failed: 'Não foi possível finalizar a encomenda. Tente novamente.',
      requiredFields: 'Preencha todos os campos obrigatórios.',
      canceled: 'O seu pagamento foi cancelado.',
    },
    product: {
      loading: 'A carregar…',
      missingId: 'Identificador do produto em falta',
      notFound: 'Produto não encontrado',
      tryCatalog: 'Experimente explorar o catálogo.',
      addToCart: '+ 🧺',
      saveForLater: 'Guardar para mais tarde',
      ingredients: 'Ingredientes',
      ingredientsList: [
        'Farinha, ovos, fermento, leite, manteiga, açúcar',
        'Pode conter vestígios de frutos secos e glúten',
        'Produzido diariamente',
      ],
      packagingNote: '* A embalagem pode mudar tendo em consideração custos associados ao destino ou outros fatores.',
    },
    apiErrors: {
      request_failed: 'Pedido falhou. Tente novamente.',
      bad_request: 'Pedido inválido.',
      invalid_json: 'JSON inválido.',
      unsupported_media_type: 'Tipo de conteúdo não suportado.',
      payload_too_large: 'Pedido demasiado grande.',
      not_found: 'Não encontrado.',
      rate_limited: 'Demasiados pedidos. Tente novamente daqui a pouco.',
      server_error: 'Erro do servidor. Tente novamente.',
      validation_error: 'Validação falhou.',
    },
    landing: {
      nav: {
        historia: 'A História',
        encomendas: 'Encomendas',
        processo: 'Como Funciona',
        envios: 'Envios',
        faq: 'FAQ',
        encomendar: 'Encomendar',
      },
      heroTitle: 'O segredo que\ntem sabor',
      heroTitleEm: 'de resistência.',
      heroDesc: 'Artesanais, frágeis, autênticos. Os Caladinhos são a iguaria mais discreta — e mais saborosa — de Santa Maria da Feira. Agora chegam até si, onde quer que esteja.',
      heroBadge: { title: 'Portugal · Espanha · PALOP · Brasil', text: 'Frescos e embalados com cuidado' },
      strip: [
        'Produto artesanal autêntico',
        'Direto das padarias da Feira',
        'Entrega PT · ES · PALOP · Brasil',
        'Pagamento seguro via Stripe',
      ],
      animSection: {
        badge: 'Acabados de fazer',
        title: 'Do forno a lenha',
        titleEm: 'para a sua mesa',
        body: 'Exterior crocante. Interior húmido e denso. Feitos com ovos, açúcar e farinha — e noventa anos de tradição.',
      },
      historia: {
        kicker: 'Desde 1934',
        title: 'O bolinho que sobreviveu à ditadura.',
        body: [
          'Em 1934, Santa Maria da Feira vivia sob olhos atentos. Os padeiros que se reuniam em segredo criaram um eufemismo doce: “estamos a fazer os caladinhos” — estar calado, estar seguro.',
          'Três gerações depois, a receita não mudou. Só o destino: de uma cozinha escondida para o mundo.',
        ],
        caption: '"Shiu... estamos a fazer os caladinhos."',
        captionSource: 'Memória oral, Santa Maria da Feira, c. 1934',
        year: '1934',
      },
      destSelector: {
        title: 'Escolha o seu destino',
        PT: { label: 'Portugal Continental', banner: 'Envio grátis em Portugal para encomendas acima de 30€ — adiciona uma Dúzia e poupa!' },
        'PT-I': { label: 'Portugal — Ilhas', banner: 'Entrega nas Ilhas em 2–4 dias úteis. Portes incluídos no preço.' },
        ES: { label: 'Espanha', banner: 'Entrega em Espanha em 2–4 dias úteis. Portes incluídos no preço.' },
        EU: { label: 'Europa / PALOP', banner: 'Entrega internacional em 4–7 dias. Para PALOP, contacte-nos para confirmar disponibilidade.' },
        BR: { label: 'Brasil', banner: 'Entrega no Brasil em 7–14 dias via CTT/DHL.' },
      },
      products: {
        sectionLabel: 'Encomendas',
        title: 'Escolha o seu pack',
        subtitle: 'Preços com envio incluído. Selecione o destino acima.',
        highlight: 'Mais popular',
      },
      lifestyle: {
        caption1: 'Da padaria para a sua mesa',
        caption2: 'Embalado com cuidado',
      },
      shipping: {
        sectionLabel: 'Política de Envios',
        title: 'Tudo sobre',
        titleEm: 'o seu envio',
        subTitle: 'Zonas e prazos',
        subtitle: 'Todos os preços incluem portes. Prazos contados após expedição.',
        th: { dest: 'Destino', time: 'Prazo', halfDozen: '6 un.', dozen: '12 un.', giftBox: '18 un.' },
        rows: [
          { flag: '🇵🇹', dest: 'Portugal Continental', time: '24–48h', halfDozen: '9,90 €', dozen: '14,90 €', giftBox: '24,90 €' },
          { flag: '🇵🇹', dest: 'Portugal — Ilhas', time: '2–4 dias', halfDozen: '14,90 €', dozen: '19,90 €', giftBox: '29,90 €' },
          { flag: '🇪🇸', dest: 'Espanha', time: '2–4 dias', halfDozen: '14,90 €', dozen: '19,90 €', giftBox: '29,90 €' },
          { flag: '🌍', dest: 'Europa & PALOP', time: '4–7 dias', halfDozen: '19,90 €', dozen: '24,90 €', giftBox: '34,90 €' },
          { flag: '🇧🇷', dest: 'Brasil', time: '7–14 dias', halfDozen: '29,90 €', dozen: '34,90 €', giftBox: '49,90 €' },
        ],
        footnote: '* Todos os preços incluem portes. Prazos contados após expedição.',
        infoTitle: 'Informações importantes',
        infoItems: [
          'Os pedidos são expedidos de segunda a sexta-feira.',
          'Receberá um email com número de tracking após expedição.',
          'Em caso de extravio ou dano, contacte-nos no prazo de 48h após a entrega prevista.',
          'Para encomendas de grande volume (+5 caixas), contacte-nos para condições especiais.',
        ],
      },
      processo: {
        sectionLabel: 'Como funciona',
        title: 'Da encomenda à porta',
        steps: [
          { title: 'Encomende online', body: 'Escolha o pack, selecione o destino e pague em segurança via Stripe.' },
          { title: 'Fazemos fresquinhos', body: 'A sua encomenda é preparada no dia seguinte à receção do pagamento.' },
          { title: 'Embalamos com cuidado', body: 'Caixa térmica, reforçada para proteger cada caladinho na viagem.' },
          { title: 'Entregue na sua porta', body: 'Receberá email de tracking assim que a encomenda for expedida.' },
        ],
        transparencia: {
          label: 'Transparência total',
          body: 'Não escondemos nada. O preço que vê é o preço que paga — envio incluído.',
        },
      },
      testemunhos: {
        sectionLabel: 'O que dizem',
        title: 'Quem já provou, volta.',
        items: [
          { text: '"Recebi em casa e em dois dias já tinha feito nova encomenda. Impossível resistir."', author: 'Ana R. · Lisboa' },
          { text: '"Enviei para o Brasil para os meus pais. Chegaram perfeitos. Chorámos um bocado."', author: 'Filipe M. · São Paulo' },
          { text: '"O sabor é exatamente o que me lembro de comer em Santa Maria da Feira em criança."', author: 'Susana C. · Porto' },
        ],
      },
      faq: {
        sectionLabel: 'FAQ',
        title: 'Perguntas frequentes',
        items: [
          { q: 'Quanto tempo demoram a chegar?', a: 'Portugal Continental: 24–48h. Espanha: 2–4 dias. Europa/PALOP: 4–7 dias. Brasil: 7–14 dias. Todos os prazos são contados após expedição.' },
          { q: 'Os Caladinhos chegam frescos?', a: 'Sim. Recolhemos nas padarias no próprio dia ou no dia anterior ao envio. A embalagem kraft mantém a frescura durante o transporte.' },
          { q: 'Qual é a validade?', a: 'Os Caladinhos autênticos são melhores nos primeiros 5 dias, mas mantêm-se até 10 dias à temperatura ambiente. A textura interior melhora ligeiramente com o tempo.' },
          { q: 'Posso encomendar para oferecer?', a: 'Sim — a Caixa Presente é especialmente pensada para isso. Inclui 24 Caladinhos e um cartão com a história de 1934. Pode indicar uma morada de entrega diferente no checkout.' },
          { q: 'Quais os métodos de pagamento?', a: 'Cartão de crédito/débito via Stripe (Visa, Mastercard, American Express). Pagamento 100% seguro e encriptado.' },
          { q: 'Fazem envios para fora da Europa?', a: 'Fazemos envios para o Brasil e PALOP. Para outros destinos, contacte-nos via WhatsApp antes de encomendar.' },
          { q: 'Posso cancelar ou alterar a minha encomenda?', a: 'Pode cancelar ou alterar até 2 horas após a encomenda, entrando em contacto via WhatsApp. Após recolha nas padarias não é possível cancelar.' },
          { q: 'Os Caladinhos têm glúten?', a: 'Sim — contêm farinha de trigo. Não são adequados para pessoas com doença celíaca ou intolerância ao glúten.' },
        ],
      },
      cta: {
        sectionLabel: 'Pronto?',
        title: 'Prove o bolinho que',
        titleEm: 'sobreviveu à ditadura.',
        body: 'Autênticos, artesanais, com história. Entregues em Portugal, Espanha, PALOP e Brasil.',
        button: 'Ver encomendas',
      },
      footer: {
        col1Title: 'Navegação',
        col1Links: [
          { label: 'A História', anchor: 'historia' },
          { label: 'Encomendas', anchor: 'produtos' },
          { label: 'Como Funciona', anchor: 'processo' },
          { label: 'FAQ', anchor: 'faq' },
          { label: 'Política de Envios', anchor: 'envios' },
        ],
        col2Title: 'Contacto',
        col2Links: [
          { label: 'ola@caladinhos.pt', href: 'mailto:ola@caladinhos.pt' },
          { label: 'Fale connosco no WhatsApp', href: 'https://wa.me/351914689252' },
          { label: 'Instagram', href: 'https://instagram.com/caladinhos.pt' },
          { label: 'Facebook', href: 'https://facebook.com/caladinhos' },
        ],
        bottomTagline: 'Doçaria portuguesa com história',
      },
    },
  },
  en: {
    localeLabel: 'English',
    nav: [
      { id: 'catalog', label: 'Catalog' },
      { id: 'taste', label: 'I want to taste' },
      { id: 'why-caladinhos', label: 'Why Caladinhos' },
      { id: 'who-we-are', label: 'Who we are' },
      { id: 'story', label: 'Who we are' },
      { id: 'news', label: 'News' },
      { id: 'shop', label: 'Shop' },
      { id: 'contact', label: 'Contact' },
    ],
    header: {
      brand: 'Caladinhos',
      tagline: 'Artisan cookies from Santa Maria da Feira, delivered worldwide. Because some flavours are pure nostalgia.',
    },
    ui: {
      cart: 'Cart',
      menu: 'Menu',
      toggleMenu: 'Toggle menu',
      language: 'Language',
      close: 'Close',
      home: 'Home',
    },
    hero: {
      kicker: 'Caladinhos Pastry House',
      title: 'Silence has never been so delightful.',
      description:
        'Discover Santa Maria da Feira\'s best-kept secret. Handmade biscuits, made with love and tradition, delivered to your door.',
      primaryCta: 'Browse catalog',
      secondaryCta: 'See news',
      stats: [
        { value: '120+', label: 'Seasonal recipes', img: '/apps/ca/img/decor/bread_transparent_04.png' },
        { value: '36h', label: 'From oven to door', img: '/apps/ca/img/decor/bread_transparent_05.png' },
        { value: '4.9★', label: 'Customer rating', img: '/apps/ca/img/decor/bread_transparent_06.png' },
      ],
    },
    hero2: {
      title: 'A Legend Born in a Whisper',
      description:
        'In 1934, to protect secret meetings, the baker would ask for silence: “Shiu... we are making caladinhos.” The silence that once protected is now the best way to share and savor this unique moment.',
      primaryCta: 'Who we are',
    },
    catalog: {
      title: 'Moments of silence to savor and share.',
      description:
        'Timeless staples and modern twists baked to order, boxed with temperature control for safe journeys.',
      cta: 'Pre-order now',
      loading: 'Loading products…',
      loadError: 'Failed to load catalog',
      badges: {
        bestseller: 'Bestseller',
        classic: 'Classic',
      },
      fallbackCategory: 'Catalog',
      items: [
        {
          id: 'classic',
          title: 'Classic Caladinho',
          description: 'Buttery sponge with citrus zest and a crystalline sugar shell.',
          price: '€18 / box',
          tag: 'bestseller',
        },
        {
          id: 'almond',
          title: 'Almond Fogaça',
          description: 'Spelt flour, toasted praliné, and honey inspired by Feira fairs.',
          price: '€22 / box',
          tag: 'limited',
        },
        {
          id: 'citrus',
          title: 'Citrus Pillow',
          description: 'Feather-light puff pastry filled with bitter orange and Azorean vanilla.',
          price: '€19 / 6 pcs',
          tag: 'new',
        },
        {
          id: 'vegan',
          title: 'Plant-Based Caladinho',
          description: 'Oat milk, muscovado sugar, and PDO olive oil for a lighter profile.',
          price: '€20 / box',
          tag: 'plant based',
        },
      ],
    },
    heroSwiper: {
      regionLabel: 'Homepage hero images',
      imageAlt: 'Caladinhos hero image',
      overlayTitle: 'Roots shaped over centuries',
      overlayCta: 'Learn more',
      goToSlide: 'Go to slide {n}',
    },
    whoWeAre: {
      kicker: 'Who we are',
      title: 'Three Generations, One Recipe — The secret time could not silence.',
      intro:
        'In 1934, in a small kitchen in Santa Maria da Feira, more than a biscuit was born: a symbol of unity and resilience. Legend says the name came from a whisper — “Shiu... caladinhos” — to protect the gatherings of those who dreamed of freer times.',
      essentialsIntro:
        'Three generations later, we continue to honor that respectful silence toward tradition. We do not give up what matters most:',
      essentials: [
        {
          title: 'Time',
          body: 'We respect the dough’s rest and the oven’s slow heat to ensure the perfect texture.',
        },
        {
          title: 'Origin',
          body: 'We select only local ingredients, from pure butter to eggs from free‑range hens.',
        },
        {
          title: 'Handmade Touch',
          body: 'Each caladinho is made in Santa Maria da Feira and passes through our hands before reaching your table, keeping the original know‑how alive at every step.',
        },
      ],
      closing:
        'Today, the silence that once kept a secret is now an invitation. An invitation to pause, share, and savor what is genuine.',
    },
    story: {
      title: 'Artisanal roots, digital future',
      subtitle: 'From Santa Maria da Feira markets to global shipping',
      paragraphs: [
        'Caladinhos started in 1963 inside a family kitchen near Santa Maria da Feira. Three generations later we still rely on linen proofing baskets, local butter, and free-range eggs.',
        'Our online store powers custom orders, hybrid tasting sessions, and corporate gifting while keeping craftsmanship at the forefront of every batch.',
      ],
      processTitle: 'Process',
      processSteps: [
        'Certified local sourcing',
        'Slow fermentation for airy crumb',
        'Thermal-ready packaging with tracking',
      ],
      processNote: '* Workflow adapts to every order',
    },
    news: {
      title: 'Pastry news',
      description: 'Events, collaborations, and launches blending tradition with innovation.',
      button: 'Read more →',
      posts: [
        {
          id: 'market',
          title: 'Caladinhos pop-up at LX Factory',
          excerpt: 'Exclusive tasting with Vinho Verde pairings and a syrup workshop.',
          date: '12 Jan 2026',
          badge: 'event',
        },
        {
          id: 'collab',
          title: 'Collab with Porto chocolatiers',
          excerpt: 'New Caladinho filled with single-origin cocoa ganache and sea salt.',
          date: '02 Jan 2026',
          badge: 'collab',
        },
        {
          id: 'award',
          title: 'Great Taste Award for Chestnut edition',
          excerpt: 'Autumn recipe praised for silky texture and spice balance.',
          date: '15 Dec 2025',
          badge: 'award',
        },
      ],
    },
    shop: {
      title: 'Shop ready to ship',
      description:
        'Weekly pre-orders, thermal packaging, and refrigerated shipping keep everything pristine.',
      bullets: [
        'Express delivery across Portugal & Spain',
        'Secure checkout with cards, MBWay, and PayPal',
        'Corporate gifting and personalized boxes',
      ],
      badge: 'Open 24/7',
      cta: 'Book a digital tasting',
      logisticsTitle: 'Premium logistics',
      logisticsDescription:
        'We track temperature, humidity, and delivery milestones in real time to guarantee consistent experiences for retail and B2B clients alike.',
      logisticsFeatures: [
        'Cold-chain partners',
        'Packaging studio',
        'Live tracking',
        '24h support',
      ],
    },
    contact: {
      title: 'Talk with our pastry team',
      description: 'We reply within one business day and love tailoring sweet experiences.',
      form: {
        name: 'Full name',
        email: 'Work email',
        message: 'Message / brief',
        send: 'Send request',
      },
      info: {
        address: 'Caladinhos Atelier, Rua Castelo 42, Santa Maria da Feira',
        schedule: 'Mon to Fri, 08:00 - 18:00',
        phone: '+351 939 000 321',
        email: 'hello@caladinhos.pt',
      },
      labels: {
        atelier: 'Atelier',
        schedule: 'Schedule',
        phone: 'Phone',
        email: 'Email',
      },
      note: 'Need B2B logistics? We craft custom catalogs with 7-day lead time.',
    },
    footer: {
      rights: '© 2026 caladinhos.pt - All rights reserved.',
      tagline: 'Caladinhos • regional pastry shop',
    },
    cartPage: {
      title: 'Your Cart',
      empty: 'Your cart is empty.',
      browseCatalog: 'Browse catalog',
      qty: 'Qty',
      remove: 'Remove',
      orderSummary: 'Order Summary',
      total: 'Total',
      proceedToCheckout: 'Proceed to checkout',
    },
    checkout: {
      title: 'Checkout',
      emptyCartAlert: 'Your cart is empty.',
      fullName: 'Full name',
      email: 'Email',
      addressLine1: 'Address line 1',
      addressLine2: 'Address line 2',
      city: 'City',
      postalCode: 'Zip code',
      cardNumber: 'Card number',
      cardExpiry: 'MM/YY',
      cardCvc: 'CVC',
      placingOrder: 'Placing order…',
      placeOrder: 'Place order',
      thankYou: 'Thank you! Order #{orderId} created.',
      failed: 'Failed to place order. Please try again.',
      requiredFields: 'Please fill all required fields.',
      canceled: 'Your payment was canceled.',
    },
    product: {
      loading: 'Loading…',
      missingId: 'Missing product id',
      notFound: 'Product not found',
      tryCatalog: 'Try exploring the catalog.',
      addToCart: '+ 🧺',
      saveForLater: 'Save for later',
      ingredients: 'Ingredients',
      ingredientsList: [
        'Flour, eggs, yeast, milk, butter, sugar',
        'May contain traces of nuts and gluten',
        'Made fresh daily',
      ],
      packagingNote: '* Packaging may change depending on destination costs or other factors.',
    },
    apiErrors: {
      request_failed: 'Request failed. Please try again.',
      bad_request: 'Bad request.',
      invalid_json: 'Invalid JSON body.',
      unsupported_media_type: 'Unsupported content type.',
      payload_too_large: 'Request body too large.',
      not_found: 'Not found.',
      rate_limited: 'Too many requests. Please try again soon.',
      server_error: 'Server error. Please try again.',
      validation_error: 'Validation failed.',
    },
    landing: {
      nav: {
        historia: 'The Story',
        encomendas: 'Orders',
        processo: 'How it works',
        envios: 'Shipping',
        faq: 'FAQ',
        encomendar: 'Order now',
      },
      heroTitle: 'The secret that\ntastes like',
      heroTitleEm: 'resistance.',
      heroDesc: 'Artisan, fragile, authentic. Caladinhos are the most discreet — and most delicious — delicacy from Santa Maria da Feira. Now delivered to you, wherever you are.',
      heroBadge: { title: 'Portugal · Spain · PALOP · Brazil', text: 'Fresh and carefully packaged' },
      strip: [
        'Authentic handmade product',
        'Direct from Feira bakeries',
        'Delivery PT · ES · PALOP · Brazil',
        'Secure payment via Stripe',
      ],
      animSection: {
        badge: 'Just made',
        title: 'From the wood-fired oven',
        titleEm: 'to your table',
        body: 'Crispy outside. Moist and dense inside. Made with eggs, sugar and flour — and ninety years of tradition.',
      },
      historia: {
        kicker: 'Since 1934',
        title: 'The cookie that survived the dictatorship.',
        body: [
          'In 1934, Santa Maria da Feira lived under watchful eyes. Bakers meeting in secret created a sweet code: “we’re making caladinhos” — stay quiet, stay safe.',
          'Three generations later, the recipe hasn’t changed. Only the destination: from a hidden kitchen to the world.',
        ],
        caption: '"Shiu... we\'re making the caladinhos."',
        captionSource: 'Oral memory, Santa Maria da Feira, c. 1934',
        year: '1934',
      },
      destSelector: {
        title: 'Choose your destination',
        PT: { label: 'Portugal (mainland)', banner: 'Free shipping in Portugal for orders over €30.' },
        'PT-I': { label: 'Portugal — Islands', banner: 'Delivery to the Islands in 2–4 business days. Shipping included.' },
        ES: { label: 'Spain', banner: 'Delivery in Spain in 2–4 business days. Shipping included.' },
        EU: { label: 'Europe / PALOP', banner: 'International delivery in 4–7 days. For PALOP, contact us to confirm availability.' },
        BR: { label: 'Brazil', banner: 'Delivery in Brazil in 7–14 days via CTT/DHL.' },
      },
      products: {
        sectionLabel: 'Orders',
        title: 'Choose your pack',
        subtitle: 'Prices include shipping. Select your destination above.',
        highlight: 'Most popular',
      },
      lifestyle: {
        caption1: 'From the bakery to your table',
        caption2: 'Packed with care',
      },
      shipping: {
        sectionLabel: 'Shipping Policy',
        title: 'Everything about',
        titleEm: 'your delivery',
        subTitle: 'Zones and timelines',
        subtitle: 'All prices include shipping. No surprises at checkout.',
        th: { dest: 'Destination', time: 'Timeline', halfDozen: '6 un.', dozen: '12 un.', giftBox: '18 un.' },
        rows: [
          { flag: '🇵🇹', dest: 'Mainland Portugal', time: '24–48h', halfDozen: '€9.90', dozen: '€14.90', giftBox: '€24.90' },
          { flag: '🇵🇹', dest: 'Portugal — Islands', time: '2–4 days', halfDozen: '€14.90', dozen: '€19.90', giftBox: '€29.90' },
          { flag: '🇪🇸', dest: 'Spain', time: '2–4 days', halfDozen: '€14.90', dozen: '€19.90', giftBox: '€29.90' },
          { flag: '🌍', dest: 'Europe & PALOP', time: '4–7 days', halfDozen: '€19.90', dozen: '€24.90', giftBox: '€34.90' },
          { flag: '🇧🇷', dest: 'Brazil', time: '7–14 days', halfDozen: '€29.90', dozen: '€34.90', giftBox: '€49.90' },
        ],
        footnote: '* All prices include shipping. Timelines counted after dispatch.',
        infoTitle: 'Important information',
        infoItems: [
          'Orders are dispatched Monday to Friday.',
          'You will receive a tracking email after dispatch.',
          'In case of loss or damage, contact us within 48h of the expected delivery date.',
          'For large orders (+5 boxes), contact us for special pricing.',
        ],
      },
      processo: {
        sectionLabel: 'How it works',
        title: 'From order to door',
        steps: [
          { title: 'Order online', body: 'Choose your pack, select destination and pay securely via Stripe.' },
          { title: 'We bake fresh', body: 'Your order is prepared the day after payment is received.' },
          { title: 'Packed with care', body: 'Thermal box, reinforced to protect each caladinho on its journey.' },
          { title: 'Delivered to your door', body: 'You\'ll receive a tracking email as soon as the order is dispatched.' },
        ],
        transparencia: {
          label: 'Full transparency',
          body: 'No hidden fees. The price you see is the price you pay — shipping included.',
        },
      },
      testemunhos: {
        sectionLabel: 'What they say',
        title: 'Once tried, always coming back.',
        items: [
          { text: '"Received at home and two days later I had already placed another order. Impossible to resist."', author: 'Ana R. · Lisbon' },
          { text: '"Shipped to Brazil for my parents. Arrived perfect. We cried a little."', author: 'Filipe M. · São Paulo' },
          { text: '"The flavour is exactly what I remember eating in Santa Maria da Feira as a child."', author: 'Susana C. · Porto' },
        ],
      },
      faq: {
        sectionLabel: 'FAQ',
        title: 'Frequently asked questions',
        items: [
          { q: 'How long does delivery take?', a: 'Mainland Portugal: 24–48h. Spain: 2–4 days. Europe/PALOP: 4–7 days. Brazil: 7–14 days. All timelines are counted after dispatch.' },
          { q: 'Do the Caladinhos arrive fresh?', a: 'Yes. We collect from the bakeries on the day of or the day before dispatch. The kraft packaging preserves freshness during transit.' },
          { q: 'What is the shelf life?', a: 'Caladinhos are best within the first 5 days, but keep for up to 10 days at room temperature. The interior texture improves slightly over time.' },
          { q: 'Can I order as a gift?', a: 'Yes — the Gift Box is designed for this. It includes 24 Caladinhos and a card with the 1934 story. You can enter a different delivery address at checkout.' },
          { q: 'What payment methods are accepted?', a: 'Credit/debit card via Stripe (Visa, Mastercard, American Express). 100% secure and encrypted payment.' },
          { q: 'Do you ship outside Europe?', a: 'We ship to Brazil and PALOP. For other destinations, please contact us via WhatsApp before ordering.' },
          { q: 'Can I cancel or change my order?', a: 'You can cancel or change within 2 hours of ordering by contacting us via WhatsApp. After collection from the bakeries, cancellation is not possible.' },
          { q: 'Do Caladinhos contain gluten?', a: 'Yes — they contain wheat flour. They are not suitable for people with coeliac disease or gluten intolerance.' },
        ],
      },
      cta: {
        sectionLabel: 'Ready?',
        title: 'Try the cookie that',
        titleEm: 'survived the dictatorship.',
        body: 'Authentic, handmade, with history. Delivered to Portugal, Spain, PALOP and Brazil.',
        button: 'View orders',
      },
      footer: {
        col1Title: 'Navigation',
        col1Links: [
          { label: 'The Story', anchor: 'historia' },
          { label: 'Orders', anchor: 'produtos' },
          { label: 'How it Works', anchor: 'processo' },
          { label: 'FAQ', anchor: 'faq' },
          { label: 'Shipping Policy', anchor: 'envios' },
        ],
        col2Title: 'Contact',
        col2Links: [
          { label: 'ola@caladinhos.pt', href: 'mailto:ola@caladinhos.pt' },
          { label: 'Chat with us on WhatsApp', href: 'https://wa.me/351914689252' },
          { label: 'Instagram', href: 'https://instagram.com/caladinhos.pt' },
          { label: 'Facebook', href: 'https://facebook.com/caladinhos' },
        ],
        bottomTagline: 'Portuguese pastry with history',
      },
    },
  },
  fr: {
    localeLabel: 'Français',
    nav: [
      { id: 'catalog', label: 'Catalogue' },
      { id: 'taste', label: 'Je veux goûter' },
      { id: 'why-caladinhos', label: 'Pourquoi Caladinhos' },
      { id: 'who-we-are', label: 'Qui sommes-nous' },
      { id: 'story', label: 'Notre histoire' },
      { id: 'news', label: 'Actualités' },
      { id: 'shop', label: 'Boutique' },
      { id: 'contact', label: 'Contact' },
    ],
    header: {
      brand: 'Caladinhos',
      tagline: 'Biscuits artisanaux de Santa Maria da Feira, livrés dans le monde entier. Parce que certaines saveurs sont de la nostalgie pure.',
    },
    ui: {
      cart: 'Panier',
      menu: 'Menu',
      toggleMenu: 'Basculer le menu',
      language: 'Langue',
      close: 'Fermer',
      home: 'Accueil',
    },
    hero: {
      kicker: 'Maison Caladinhos',
      title: 'Le silence n’a jamais été aussi délicieux.',
      description:
        'Découvrez le secret le mieux gardé de Santa Maria da Feira. Biscuits artisanaux, faits avec amour et tradition, livrés à votre porte.',
      primaryCta: 'Explorer le catalogue',
      secondaryCta: 'Voir les actualités',
      stats: [
        { value: '120+', label: 'Recettes saisonnières', img: '/apps/ca/img/decor/bread_transparent_04.png' },
        { value: '36h', label: 'Du four à la porte', img: '/apps/ca/img/decor/bread_transparent_05.png' },
        { value: '4.9★', label: 'Note client', img: '/apps/ca/img/decor/bread_transparent_06.png' },
      ],
    },
    hero2: {
      title: 'Une légende née d’un murmure',
      description:
        'En 1934, pour protéger des réunions secrètes, le boulanger demandait le silence : « Shiu... nous faisons des caladinhos ». Le silence qui protégeait autrefois est aujourd’hui la meilleure façon de partager et savourer ce moment unique.',
      primaryCta: 'Qui sommes-nous',
    },
    catalog: {
      title: 'Des instants de silence à savourer et partager.',
      description:
        'Des classiques intemporels et des créations exclusives, fabriqués en petites séries et emballés pour des voyages sûrs.',
      cta: 'Précommander',
      loading: 'Chargement des produits…',
      loadError: 'Impossible de charger le catalogue',
      badges: {
        bestseller: 'Best‑seller',
        classic: 'Classique',
      },
      fallbackCategory: 'Catalogue',
      items: [
        {
          id: 'classic',
          title: 'Caladinho Classique',
          description: 'Pâte moelleuse au beurre, note d’agrumes et glaçage cristallisé.',
          price: '18€ / boîte',
          tag: 'best‑seller',
        },
        {
          id: 'almond',
          title: 'Fogaça aux amandes',
          description: 'Farine d’épeautre et praliné croquant, inspirés des foires de Feira.',
          price: '22€ / boîte',
          tag: 'édition limitée',
        },
        {
          id: 'citrus',
          title: 'Coussin citronné',
          description: 'Feuilleté léger à la confiture d’orange amère et vanille des Açores.',
          price: '19€ / 6 pcs',
          tag: 'nouveau',
        },
        {
          id: 'vegan',
          title: 'Caladinho végétal',
          description: 'Lait d’avoine, sucre muscovado et huile d’olive AOP pour un profil plus léger.',
          price: '20€ / boîte',
          tag: 'plant based',
        },
      ],
    },
    heroSwiper: {
      regionLabel: 'Images héro de la page d’accueil',
      imageAlt: 'Image héro Caladinhos',
      overlayTitle: 'Des racines façonnées au fil des siècles',
      overlayCta: 'En savoir plus',
      goToSlide: 'Aller à la diapositive {n}',
    },
    whoWeAre: {
      kicker: 'Qui sommes-nous',
      title: 'Trois générations, une recette — Le secret que le temps n’a pas fait taire.',
      intro:
        'En 1934, dans une petite cuisine de Santa Maria da Feira, naissait plus qu’un biscuit : un symbole d’union et de résilience. La légende raconte que le nom vient d’un murmure — « Shiu... caladinhos » — pour protéger les rencontres de ceux qui rêvaient de temps plus libres.',
      essentialsIntro:
        'Trois générations plus tard, nous continuons d’honorer ce silence respectueux envers la tradition. Nous ne renonçons pas à l’essentiel :',
      essentials: [
        {
          title: 'Le temps',
          body: 'Nous respectons le repos de la pâte et la chaleur douce du four pour une texture parfaite.',
        },
        {
          title: 'L’origine',
          body: 'Nous sélectionnons uniquement des ingrédients locaux, du beurre pur aux œufs de poules élevées en plein air.',
        },
        {
          title: 'Le geste artisanal',
          body: 'Chaque caladinho est façonné à Santa Maria da Feira et passe entre nos mains avant d’arriver à votre table, gardant le savoir‑faire vivant à chaque étape.',
        },
      ],
      closing:
        'Aujourd’hui, le silence qui gardait un secret est devenu une invitation. Une invitation à faire une pause, partager et savourer l’authentique.',
    },
    story: {
      title: 'Racines artisanales, vision contemporaine',
      subtitle: 'De Santa Maria da Feira au monde digital',
      paragraphs: [
        'Caladinhos est né en 1963 dans une cuisine familiale à Santa Maria da Feira. Trois générations plus tard, nous utilisons toujours des paniers de fermentation en lin, du beurre local et des œufs de poules élevées en plein air.',
        'Notre boutique en ligne permet des commandes personnalisées, des dégustations hybrides et des services corporatifs, tout en gardant la maîtrise artisanale à chaque étape de production.',
      ],
      processTitle: 'Processus',
      processSteps: [
        'Sélection d’ingrédients locaux certifiés',
        'Fermentations longues pour une texture aérienne',
        'Emballage thermique avec suivi',
      ],
      processNote: '* Flux adaptable à chaque commande',
    },
    news: {
      title: 'Actualités gourmandes',
      description: 'Événements, collaborations et lancements entre tradition et innovation.',
      button: 'En savoir plus →',
      posts: [
        {
          id: 'market',
          title: 'Pop‑up Caladinhos à la LX Factory',
          excerpt: 'Dégustation exclusive avec accords de vinhos verdes et atelier sur les sirops.',
          date: '12 Jan 2026',
          badge: 'événement',
        },
        {
          id: 'collab',
          title: 'Collaboration avec des chocolatiers de Porto',
          excerpt: 'Nouveau Caladinho fourré d’une ganache de cacao d’origine unique et fleur de sel.',
          date: '02 Jan 2026',
          badge: 'collab',
        },
        {
          id: 'award',
          title: 'Prix Great Taste pour l’édition Châtaigne',
          excerpt: 'Recette d’automne primée pour sa texture soyeuse et son équilibre d’épices.',
          date: '15 Déc 2025',
          badge: 'prix',
        },
      ],
    },
    shop: {
      title: 'Boutique en ligne prête à expédier',
      description:
        'Précommandes hebdomadaires, emballage thermique et logistique réfrigérée pour une fraîcheur maximale.',
      bullets: [
        'Livraisons express au Portugal et en Espagne',
        'Paiement sécurisé, cartes, MBWay et PayPal',
        'Cadeaux d’entreprise et coffrets personnalisés',
      ],
      badge: 'Ouvert 24/7',
      cta: 'Réserver une dégustation digitale',
      logisticsTitle: 'Logistique premium',
      logisticsDescription:
        'Nous suivons la température, l’humidité et les étapes de livraison en temps réel pour garantir une expérience constante, B2C comme B2B.',
      logisticsFeatures: [
        'Partenaires de chaîne du froid',
        'Studio de packaging',
        'Suivi en temps réel',
        'Support 24h',
      ],
    },
    contact: {
      title: 'Parlez avec notre équipe pâtisserie',
      description: 'Nous répondons sous 24h ouvrées et aimons créer des expériences sur‑mesure.',
      form: {
        name: 'Nom complet',
        email: 'Email professionnel',
        message: 'Message / brief',
        send: 'Envoyer la demande',
      },
      info: {
        address: 'Atelier Caladinhos, Rua Castelo 42, Santa Maria da Feira',
        schedule: 'Lun à ven, 08:00 - 18:00',
        phone: '+351 939 000 321',
        email: 'hello@caladinhos.pt',
      },
      labels: {
        atelier: 'Atelier',
        schedule: 'Horaires',
        phone: 'Téléphone',
        email: 'Email',
      },
      note: 'Besoin d’une logistique B2B ? Nous créons des catalogues sur mesure avec un délai de 7 jours.',
    },
    footer: {
      rights: '© 2026 caladinhos.pt - Tous droits réservés.',
      tagline: 'Caladinhos • pâtisserie régionale',
    },
    cartPage: {
      title: 'Panier',
      empty: 'Votre panier est vide.',
      browseCatalog: 'Parcourir le catalogue',
      qty: 'Qté',
      remove: 'Supprimer',
      orderSummary: 'Récapitulatif',
      total: 'Total',
      proceedToCheckout: 'Passer au paiement',
    },
    checkout: {
      title: 'Paiement',
      emptyCartAlert: 'Votre panier est vide.',
      fullName: 'Nom complet',
      email: 'Email',
      addressLine1: 'Adresse',
      addressLine2: 'Adresse (ligne 2)',
      city: 'Ville',
      postalCode: 'Code postal',
      cardNumber: 'Numéro de carte',
      cardExpiry: 'MM/AA',
      cardCvc: 'CVC',
      placingOrder: 'Commande en cours…',
      placeOrder: 'Passer commande',
      thankYou: 'Merci ! Commande #{orderId} créée.',
      failed: 'Échec de la commande. Veuillez réessayer.',
      requiredFields: 'Veuillez remplir tous les champs obligatoires.',
      canceled: 'Votre paiement a été annulé.',
    },
    product: {
      loading: 'Chargement…',
      missingId: 'Identifiant produit manquant',
      notFound: 'Produit introuvable',
      tryCatalog: 'Essayez d’explorer le catalogue.',
      addToCart: '+ 🧺',
      saveForLater: 'Enregistrer pour plus tard',
      ingredients: 'Ingrédients',
      ingredientsList: [
        'Farine, œufs, levure, lait, beurre, sucre',
        'Peut contenir des traces de fruits à coque et de gluten',
        'Préparé chaque jour',
      ],
      packagingNote: '* L’emballage peut varier selon les coûts de destination ou d’autres facteurs.',
    },
    apiErrors: {
      request_failed: 'La requête a échoué. Veuillez réessayer.',
      bad_request: 'Requête invalide.',
      invalid_json: 'JSON invalide.',
      unsupported_media_type: 'Type de contenu non pris en charge.',
      payload_too_large: 'Corps de requête trop volumineux.',
      not_found: 'Introuvable.',
      rate_limited: 'Trop de requêtes. Réessayez bientôt.',
      server_error: 'Erreur serveur. Veuillez réessayer.',
      validation_error: 'Échec de la validation.',
    },
    landing: {
      nav: {
        historia: 'L\'Histoire',
        encomendas: 'Commandes',
        processo: 'Comment ça marche',
        envios: 'Livraisons',
        faq: 'FAQ',
        encomendar: 'Commander',
      },
      heroTitle: 'Le secret qui\na le goût de',
      heroTitleEm: 'la résistance.',
      heroDesc: 'Artisanaux, fragiles, authentiques. Les Caladinhos sont le délice le plus discret — et le plus savoureux — de Santa Maria da Feira. Maintenant livrés jusqu\'à vous, où que vous soyez.',
      heroBadge: { title: 'Portugal · Espagne · PALOP · Brésil', text: 'Frais et emballés avec soin' },
      strip: [
        'Produit artisanal authentique',
        'Direct des boulangeries de Feira',
        'Livraison PT · ES · PALOP · Brésil',
        'Paiement sécurisé via Stripe',
      ],
      animSection: {
        badge: 'Tout juste sortis du four',
        title: 'Du four à bois',
        titleEm: 'à votre table',
        body: 'Croustillant à l\'extérieur. Moelleux et dense à l\'intérieur. Fait avec des œufs, du sucre et de la farine — et quatre-vingt-dix ans de tradition.',
      },
      historia: {
        kicker: 'Depuis 1934',
        title: 'Le biscuit qui a survécu à la dictature.',
        body: [
          'En 1934, Santa Maria da Feira vivait sous des yeux attentifs. Les boulangers qui se réunissaient en secret crèrent un code sucré : “on fait les caladinhos” — rester silencieux, rester en sécurité.',
          'Trois générations plus tard, la recette n’a pas changé. Seulement la destination : d’une cuisine cachée vers le monde.',
        ],
        caption: '"Chut... on fait les caladinhos."',
        captionSource: 'Mémoire orale, Santa Maria da Feira, c. 1934',
        year: '1934',
      },
      destSelector: {
        title: 'Choisissez votre destination',
        PT: { label: 'Portugal (continent)', banner: 'Livraison gratuite au Portugal pour les commandes de plus de 30€.' },
        'PT-I': { label: 'Portugal — Îles', banner: 'Livraison aux Îles en 2–4 jours ouvrés. Frais inclus.' },
        ES: { label: 'Espagne', banner: 'Livraison en Espagne en 2–4 jours ouvrés. Frais inclus.' },
        EU: { label: 'Europe / PALOP', banner: 'Livraison internationale en 4–7 jours. Pour les PALOP, contactez-nous.' },
        BR: { label: 'Brésil', banner: 'Livraison au Brésil en 7–14 jours via CTT/DHL.' },
      },
      products: {
        sectionLabel: 'Commandes',
        title: 'Choisissez votre pack',
        subtitle: 'Prix avec livraison incluse. Sélectionnez votre destination ci-dessus.',
        highlight: 'Le plus populaire',
      },
      lifestyle: {
        caption1: 'De la boulangerie à votre table',
        caption2: 'Emballé avec soin',
      },
      shipping: {
        sectionLabel: 'Politique de Livraison',
        title: 'Tout savoir sur',
        titleEm: 'votre livraison',
        subTitle: 'Zones et délais',
        subtitle: 'Tous les prix incluent la livraison. Aucune surprise au paiement.',
        th: { dest: 'Destination', time: 'Délai', halfDozen: '6 un.', dozen: '12 un.', giftBox: '18 un.' },
        rows: [
          { flag: '🇵🇹', dest: 'Portugal (continent)', time: '24–48h', halfDozen: '9,90 €', dozen: '14,90 €', giftBox: '24,90 €' },
          { flag: '🇵🇹', dest: 'Portugal — Îles', time: '2–4 jours', halfDozen: '14,90 €', dozen: '19,90 €', giftBox: '29,90 €' },
          { flag: '🇪🇸', dest: 'Espagne', time: '2–4 jours', halfDozen: '14,90 €', dozen: '19,90 €', giftBox: '29,90 €' },
          { flag: '🌍', dest: 'Europe & PALOP', time: '4–7 jours', halfDozen: '19,90 €', dozen: '24,90 €', giftBox: '34,90 €' },
          { flag: '🇧🇷', dest: 'Brésil', time: '7–14 jours', halfDozen: '29,90 €', dozen: '34,90 €', giftBox: '49,90 €' },
        ],
        footnote: '* Tous les prix incluent la livraison. Délais comptés après expédition.',
        infoTitle: 'Informations importantes',
        infoItems: [
          'Les commandes sont expédiées du lundi au vendredi.',
          'Vous recevrez un email de suivi après expédition.',
          'En cas de perte ou de dommage, contactez-nous dans les 48h après la date de livraison prévue.',
          'Pour les grandes commandes (+5 boîtes), contactez-nous pour des conditions spéciales.',
        ],
      },
      processo: {
        sectionLabel: 'Comment ça marche',
        title: 'De la commande à votre porte',
        steps: [
          { title: 'Commandez en ligne', body: 'Choisissez votre pack, sélectionnez la destination et payez via Stripe.' },
          { title: 'Nous cuisinons frais', body: 'Votre commande est préparée le lendemain de la réception du paiement.' },
          { title: 'Emballé avec soin', body: 'Boîte thermique renforcée pour protéger chaque caladinho.' },
          { title: 'Livré chez vous', body: 'Vous recevrez un email de suivi dès l’expédition de votre commande.' },
        ],
        transparencia: {
          label: 'Transparence totale',
          body: 'Pas de frais cachés. Le prix affiché est le prix payé — livraison incluse.',
        },
      },
      testemunhos: {
        sectionLabel: 'Ce qu\'ils disent',
        title: 'Une fois goûté, on revient toujours.',
        items: [
          { text: '"Reçu à la maison et deux jours plus tard j\'avais déjà passé une nouvelle commande. Impossible de résister."', author: 'Ana R. · Lisbonne' },
          { text: '"Envoyé au Brésil pour mes parents. Arrivés parfaits. On a pleuré un peu."', author: 'Filipe M. · São Paulo' },
          { text: '"La saveur est exactement ce dont je me souviens avoir mangé à Santa Maria da Feira enfant."', author: 'Susana C. · Porto' },
        ],
      },
      faq: {
        sectionLabel: 'FAQ',
        title: 'Questions fréquentes',
        items: [
          { q: 'Combien de temps prend la livraison ?', a: 'Portugal continental : 24–48h. Espagne : 2–4 jours. Europe/PALOP : 4–7 jours. Brésil : 7–14 jours. Tous les délais sont comptés après expédition.' },
          { q: 'Les Caladinhos arrivent-ils frais ?', a: 'Oui. Nous les récollectons dans les boulangeries le jour même ou la veille de l\'expédition. L\'emballage kraft préserve la fraîcheur pendant le transport.' },
          { q: 'Quelle est la durée de conservation ?', a: 'Les Caladinhos sont meilleurs dans les 5 premiers jours, mais se conservent jusqu\'\u00e0 10 jours à température ambiante. La texture intérieure s\'améliore légèrement avec le temps.' },
          { q: 'Puis-je commander comme cadeau ?', a: 'Oui — le Coffret Cadeau est conçu pour cela. Il contient 24 Caladinhos et une carte avec l\'histoire de 1934. Vous pouvez indiquer une adresse de livraison différente au moment du paiement.' },
          { q: 'Quels modes de paiement acceptez-vous ?', a: 'Carte de crédit/débit via Stripe (Visa, Mastercard, American Express). Paiement 100 % sécurisé et crypté.' },
          { q: 'Livrez-vous hors d\'Europe ?', a: 'Nous livrons au Brésil et dans les PALOP. Pour d\'autres destinations, contactez-nous via WhatsApp avant de commander.' },
          { q: 'Puis-je annuler ou modifier ma commande ?', a: 'Vous pouvez annuler ou modifier dans les 2 heures suivant la commande en nous contactant via WhatsApp. Après collecte dans les boulangeries, l\'annulation n\'est plus possible.' },
          { q: 'Les Caladinhos contiennent-ils du gluten ?', a: 'Oui — ils contiennent de la farine de blé. Ils ne conviennent pas aux personnes atteintes de la maladie cœliaque ou intolérantes au gluten.' },
        ],
      },
      cta: {
        sectionLabel: 'Prêt(e) ?',
        title: 'Goûtez le biscuit qui',
        titleEm: 'a survécu à la dictature.',
        body: 'Authentiques, artisanaux, avec de l\'histoire. Livrés au Portugal, Espagne, PALOP et Brésil.',
        button: 'Voir les commandes',
      },
      footer: {
        col1Title: 'Navigation',
        col1Links: [
          { label: 'L\'Histoire', anchor: 'historia' },
          { label: 'Commandes', anchor: 'produtos' },
          { label: 'Comment ça marche', anchor: 'processo' },
          { label: 'FAQ', anchor: 'faq' },
          { label: 'Politique de Livraison', anchor: 'envios' },
        ],
        col2Title: 'Contact',
        col2Links: [
          { label: 'ola@caladinhos.pt', href: 'mailto:ola@caladinhos.pt' },
          { label: 'Discutez avec nous sur WhatsApp', href: 'https://wa.me/351914689252' },
          { label: 'Instagram', href: 'https://instagram.com/caladinhos.pt' },
          { label: 'Facebook', href: 'https://facebook.com/caladinhos' },
        ],
        bottomTagline: 'Pâtisserie portugaise avec de l\'histoire',
      },
    },
  },
  es: {
    localeLabel: 'Español',
    nav: [
      { id: 'catalog', label: 'Catálogo' },
      { id: 'taste', label: 'Quiero probar' },
      { id: 'why-caladinhos', label: 'Por qué Caladinhos' },
      { id: 'who-we-are', label: 'Quiénes somos' },
      { id: 'story', label: 'Nuestra historia' },
      { id: 'news', label: 'Noticias' },
      { id: 'shop', label: 'Tienda' },
      { id: 'contact', label: 'Contacto' },
    ],
    header: {
      brand: 'Caladinhos',
      tagline: 'Dulces artesanales de Santa Maria da Feira, entregados en Portugal y en el mundo. Porque hay sabores que son nostalgia.',
    },
    ui: {
      cart: 'Carrito',
      menu: 'Menú',
      toggleMenu: 'Alternar menú',
      language: 'Idioma',
      close: 'Cerrar',
      home: 'Inicio',
    },
    hero: {
      kicker: 'Casa Caladinhos',
      title: 'El silencio nunca fue tan delicioso.',
      description:
        'Descubre el secreto mejor guardado de Santa Maria da Feira. Galletas artesanales, hechas con amor y tradición, entregadas en tu puerta.',
      primaryCta: 'Explorar catálogo',
      secondaryCta: 'Ver novedades',
      stats: [
        { value: '120+', label: 'Recetas de temporada', img: '/apps/ca/img/decor/bread_transparent_04.png' },
        { value: '36h', label: 'Del horno a tu puerta', img: '/apps/ca/img/decor/bread_transparent_05.png' },
        { value: '4.9★', label: 'Valoración de clientes', img: '/apps/ca/img/decor/bread_transparent_06.png' },
      ],
    },
    hero2: {
      title: 'Una leyenda nacida de un susurro',
      description:
        'En 1934, para proteger reuniones secretas, el panadero pedía silencio: “Shiu... estamos haciendo caladinhos”. El silencio que antes protegía es ahora la mejor forma de compartir y saborear este momento único.',
      primaryCta: 'Quiénes somos',
    },
    catalog: {
      title: 'Momentos de silencio para saborear y compartir.',
      description:
        'Clásicos atemporales y creaciones exclusivas, hechos en pequeños lotes y embalados para viajes seguros.',
      cta: 'Preordenar',
      loading: 'Cargando productos…',
      loadError: 'No se pudo cargar el catálogo',
      badges: {
        bestseller: 'Más vendido',
        classic: 'Clásico',
      },
      fallbackCategory: 'Catálogo',
      items: [
        {
          id: 'classic',
          title: 'Caladinho Clásico',
          description: 'Masa esponjosa con mantequilla, toque cítrico y cobertura cristalizada.',
          price: '18€ / caja',
          tag: 'más vendido',
        },
        {
          id: 'almond',
          title: 'Fogaça de almendra',
          description: 'Harina de espelta y praliné crujiente, inspirada en las ferias de Feira.',
          price: '22€ / caja',
          tag: 'edición limitada',
        },
        {
          id: 'citrus',
          title: 'Almohadita cítrica',
          description: 'Hojaldre ligero con mermelada de naranja amarga y vainilla de las Azores.',
          price: '19€ / 6 uds',
          tag: 'nuevo',
        },
        {
          id: 'vegan',
          title: 'Caladinho vegetal',
          description: 'Leche de avena, azúcar mascabado y aceite de oliva DOP para un perfil más ligero.',
          price: '20€ / caja',
          tag: 'plant based',
        },
      ],
    },
    heroSwiper: {
      regionLabel: 'Imágenes destacadas de la página de inicio',
      imageAlt: 'Imagen destacada Caladinhos',
      overlayTitle: 'Raíces con sabor de siempre',
      overlayCta: 'Saber más',
      goToSlide: 'Ir a la diapositiva {n}',
    },
    whoWeAre: {
      kicker: 'Quiénes somos',
      title: 'Tres generaciones, una receta — El secreto que el tiempo no pudo callar.',
      intro:
        'En 1934, en una pequeña cocina de Santa Maria da Feira, nació algo más que una galleta: nació un símbolo de unión y resistencia. La leyenda dice que el nombre surgió de un susurro — “Shiu... caladinhos” — para proteger los encuentros de quienes soñaban con tiempos más libres.',
      essentialsIntro:
        'Tres generaciones después, seguimos honrando ese silencio respetuoso hacia la tradición. No renunciamos a lo esencial:',
      essentials: [
        {
          title: 'El tiempo',
          body: 'Respetamos el reposo de la masa y el calor lento del horno para garantizar la textura perfecta.',
        },
        {
          title: 'El origen',
          body: 'Seleccionamos solo ingredientes locales, desde mantequilla pura hasta huevos de gallinas camperas.',
        },
        {
          title: 'El toque manual',
          body: 'Cada caladinho se hace en Santa Maria da Feira y pasa por nuestras manos antes de llegar a tu mesa, manteniendo vivo el saber original en cada etapa.',
        },
      ],
      closing:
        'Hoy, el silencio que antes guardaba un secreto es ahora una invitación. Una invitación a parar, compartir y saborear lo auténtico.',
    },
    story: {
      title: 'Raíces artesanales, visión contemporánea',
      subtitle: 'De Santa Maria da Feira al mundo digital',
      paragraphs: [
        'Caladinhos nació en 1963 en una cocina familiar en Santa Maria da Feira. Tres generaciones después seguimos usando cestos de fermentación de lino, mantequilla local y huevos de gallinas camperas.',
        'Nuestra tienda online permite pedidos personalizados, experiencias de cata híbridas y servicios corporativos, manteniendo el control artesanal en cada etapa de la producción.',
      ],
      processTitle: 'Proceso',
      processSteps: [
        'Selección de ingredientes locales certificados',
        'Fermentaciones largas para una textura aireada',
        'Embalaje térmico con seguimiento',
      ],
      processNote: '* Flujo adaptable a cada pedido',
    },
    news: {
      title: 'Novedades dulces',
      description: 'Eventos, colaboraciones y lanzamientos que unen tradición e innovación.',
      button: 'Saber más →',
      posts: [
        {
          id: 'market',
          title: 'Pop‑up Caladinhos en LX Factory',
          excerpt: 'Degustación exclusiva con maridajes de vinos verdes y taller de siropes.',
          date: '12 Ene 2026',
          badge: 'evento',
        },
        {
          id: 'collab',
          title: 'Colaboración con maestros chocolateros de Oporto',
          excerpt: 'Nuevo Caladinho relleno de ganache de cacao de origen único y flor de sal.',
          date: '02 Ene 2026',
          badge: 'colaboración',
        },
        {
          id: 'award',
          title: 'Premio Great Taste para la edición Castaña',
          excerpt: 'Receta de otoño premiada por su textura sedosa y equilibrio de especias.',
          date: '15 Dic 2025',
          badge: 'premio',
        },
      ],
    },
    shop: {
      title: 'Tienda online lista para enviar',
      description:
        'Preórdenes semanales, embalaje térmico y logística refrigerada garantizan la máxima frescura.',
      bullets: [
        'Envíos exprés a Portugal y España',
        'Pago seguro con tarjetas, MBWay y PayPal',
        'Regalos corporativos y cajas personalizadas',
      ],
      badge: 'Abierto 24/7',
      cta: 'Reservar una cata digital',
      logisticsTitle: 'Logística premium',
      logisticsDescription:
        'Monitorizamos temperatura, humedad y hitos de entrega en tiempo real para garantizar experiencias consistentes en retail y B2B.',
      logisticsFeatures: [
        'Socios de cadena de frío',
        'Estudio de packaging',
        'Seguimiento en tiempo real',
        'Soporte 24h',
      ],
    },
    contact: {
      title: 'Habla con nuestro equipo de pastelería',
      description: 'Respondemos en menos de 24h y nos encanta diseñar experiencias a medida.',
      form: {
        name: 'Nombre completo',
        email: 'Email profesional',
        message: 'Mensaje / briefing',
        send: 'Enviar solicitud',
      },
      info: {
        address: 'Atelier Caladinhos, Rua Castelo 42, Santa Maria da Feira',
        schedule: 'Lun a vie, 08:00 - 18:00',
        phone: '+351 939 000 321',
        email: 'hello@caladinhos.pt',
      },
      labels: {
        atelier: 'Atelier',
        schedule: 'Horario',
        phone: 'Teléfono',
        email: 'Email',
      },
      note: '¿Necesitas logística B2B? Creamos catálogos personalizados con plazo de 7 días.',
    },
    footer: {
      rights: '© 2026 caladinhos.pt - Todos los derechos reservados.',
      tagline: 'Caladinhos • pastelería regional',
    },
    cartPage: {
      title: 'Carrito',
      empty: 'Tu carrito está vacío.',
      browseCatalog: 'Explorar catálogo',
      qty: 'Cant.',
      remove: 'Eliminar',
      orderSummary: 'Resumen del pedido',
      total: 'Total',
      proceedToCheckout: 'Ir al pago',
    },
    checkout: {
      title: 'Pago',
      emptyCartAlert: 'Tu carrito está vacío.',
      fullName: 'Nombre completo',
      email: 'Email',
      addressLine1: 'Dirección',
      addressLine2: 'Dirección (línea 2)',
      city: 'Ciudad',
      postalCode: 'Código postal',
      cardNumber: 'Número de tarjeta',
      cardExpiry: 'MM/AA',
      cardCvc: 'CVC',
      placingOrder: 'Realizando pedido…',
      placeOrder: 'Realizar pedido',
      thankYou: '¡Gracias! Pedido #{orderId} creado.',
      failed: 'No se pudo realizar el pedido. Inténtalo de nuevo.',
      requiredFields: 'Completa todos los campos obligatorios.',
      canceled: 'Tu pago fue cancelado.',
    },
    product: {
      loading: 'Cargando…',
      missingId: 'Falta el identificador del producto',
      notFound: 'Producto no encontrado',
      tryCatalog: 'Prueba a explorar el catálogo.',
      addToCart: '+ 🧺',
      saveForLater: 'Guardar para más tarde',
      ingredients: 'Ingredientes',
      ingredientsList: [
        'Harina, huevos, levadura, leche, mantequilla, azúcar',
        'Puede contener trazas de frutos secos y gluten',
        'Elaborado a diario',
      ],
      packagingNote: '* El embalaje puede cambiar según los costes de destino u otros factores.',
    },
    apiErrors: {
      request_failed: 'La solicitud falló. Inténtalo de nuevo.',
      bad_request: 'Solicitud incorrecta.',
      invalid_json: 'JSON inválido.',
      unsupported_media_type: 'Tipo de contenido no compatible.',
      payload_too_large: 'Cuerpo de solicitud demasiado grande.',
      not_found: 'No encontrado.',
      rate_limited: 'Demasiadas solicitudes. Inténtalo más tarde.',
      server_error: 'Error del servidor. Inténtalo de nuevo.',
      validation_error: 'La validación falló.',
    },
    landing: {
      nav: {
        historia: 'La Historia',
        encomendas: 'Pedidos',
        processo: 'Cómo funciona',
        envios: 'Envíos',
        faq: 'FAQ',
        encomendar: 'Pedir ahora',
      },
      heroTitle: 'El secreto que\ntiene sabor a',
      heroTitleEm: 'resistencia.',
      heroDesc: 'Artesanales, frágiles, auténticos. Los Caladinhos son la delicia más discreta — y más sabrosa — de Santa Maria da Feira. Ahora llegan hasta usted, donde quiera que esté.',
      heroBadge: { title: 'Portugal · España · PALOP · Brasil', text: 'Frescos y empaquetados con cuidado' },
      strip: [
        'Producto artesanal auténtico',
        'Directo de las panaderías de Feira',
        'Entrega PT · ES · PALOP · Brasil',
        'Pago seguro vía Stripe',
      ],
      animSection: {
        badge: 'Recién hechos',
        title: 'Del horno de leña',
        titleEm: 'a su mesa',
        body: 'Crujiente por fuera. Húmedo y denso por dentro. Hechos con huevos, azúcar y harina — y noventa años de tradición.',
      },
      historia: {
        kicker: 'Desde 1934',
        title: 'El dulce que sobrevivió a la dictadura.',
        body: [
          'En 1934, Santa Maria da Feira vivía bajo ojos atentos. Los panaderos que se reunían en secreto crearon un eufemismo dulce: “estamos haciendo los caladinhos” — callar, estar seguro.',
          'Tres generaciones después, la receta no ha cambiado. Solo el destino: de una cocina escondida al mundo.',
        ],
        caption: '"Shiu... estamos haciendo los caladinhos."',
        captionSource: 'Memoria oral, Santa Maria da Feira, c. 1934',
        year: '1934',
      },
      destSelector: {
        title: 'Elige tu destino',
        PT: { label: 'Portugal Continental', banner: 'Envío gratis en Portugal en pedidos superiores a 30€.' },
        'PT-I': { label: 'Portugal — Islas', banner: 'Entrega en las Islas en 2–4 días hábiles. Gastos incluidos.' },
        ES: { label: 'España', banner: 'Entrega en España en 2–4 días hábiles. Gastos incluidos.' },
        EU: { label: 'Europa / PALOP', banner: 'Entrega internacional en 4–7 días. Para PALOP, contáctanos.' },
        BR: { label: 'Brasil', banner: 'Entrega en Brasil en 7–14 días via CTT/DHL.' },
      },
      products: {
        sectionLabel: 'Pedidos',
        title: 'Elige tu pack',
        subtitle: 'Precios con envío incluido. Selecciona tu destino arriba.',
        highlight: 'Más popular',
      },
      lifestyle: {
        caption1: 'De la panadería a tu mesa',
        caption2: 'Empaquetado con cuidado',
      },
      shipping: {
        sectionLabel: 'Política de Envíos',
        title: 'Todo sobre',
        titleEm: 'su envío',
        subTitle: 'Zonas y plazos',
        subtitle: 'Todos los precios incluyen el envío. Sin sorpresas en el pago.',
        th: { dest: 'Destino', time: 'Plazo', halfDozen: '6 un.', dozen: '12 un.', giftBox: '18 un.' },
        rows: [
          { flag: '🇵🇹', dest: 'Portugal Continental', time: '24–48h', halfDozen: '9,90 €', dozen: '14,90 €', giftBox: '24,90 €' },
          { flag: '🇵🇹', dest: 'Portugal — Islas', time: '2–4 días', halfDozen: '14,90 €', dozen: '19,90 €', giftBox: '29,90 €' },
          { flag: '🇪🇸', dest: 'España', time: '2–4 días', halfDozen: '14,90 €', dozen: '19,90 €', giftBox: '29,90 €' },
          { flag: '🌍', dest: 'Europa & PALOP', time: '4–7 días', halfDozen: '19,90 €', dozen: '24,90 €', giftBox: '34,90 €' },
          { flag: '🇧🇷', dest: 'Brasil', time: '7–14 días', halfDozen: '29,90 €', dozen: '34,90 €', giftBox: '49,90 €' },
        ],
        footnote: '* Todos los precios incluyen gastos de envío. Plazos contados tras el envío.',
        infoTitle: 'Información importante',
        infoItems: [
          'Los pedidos se envían de lunes a viernes.',
          'Recibirá un email con número de seguimiento tras el envío.',
          'En caso de extravio o daño, contáctenos en 48h tras la fecha de entrega prevista.',
          'Para pedidos grandes (+5 cajas), contáctenos para condiciones especiales.',
        ],
      },
      processo: {
        sectionLabel: 'Cómo funciona',
        title: 'Del pedido a tu puerta',
        steps: [
          { title: 'Haz tu pedido online', body: 'Elige tu pack, selecciona el destino y paga con Stripe.' },
          { title: 'Lo hacemos fresquito', body: 'Tu pedido se prepara al día siguiente de recibir el pago.' },
          { title: 'Empaquetado con cuidado', body: 'Caja térmica reforzada para proteger cada caladinho.' },
          { title: 'Entregado en tu puerta', body: 'Recibirás un email de seguimiento cuando se envíe tu pedido.' },
        ],
        transparencia: {
          label: 'Transparencia total',
          body: 'Sin costes ocultos. El precio que ves es el que pagas — envío incluido.',
        },
      },
      testemunhos: {
        sectionLabel: 'Qué dicen',
        title: 'Una vez probado, siempre se vuelve.',
        items: [
          { text: '"Lo recibí en casa y dos días después ya había hecho otro pedido. Imposible resistirse."', author: 'Ana R. · Lisboa' },
          { text: '"Lo envié a Brasil para mis padres. Llegaron perfectos. Lloramos un poco."', author: 'Filipe M. · São Paulo' },
          { text: '"El sabor es exactamente lo que recuerdo comer en Santa Maria da Feira de niña."', author: 'Susana C. · Oporto' },
        ],
      },
      faq: {
        sectionLabel: 'FAQ',
        title: 'Preguntas frecuentes',
        items: [
          { q: '¿Cuánto tiempo tarda la entrega?', a: 'Portugal Continental: 24–48h. España: 2–4 días. Europa/PALOP: 4–7 días. Brasil: 7–14 días. Todos los plazos se cuentan tras el envío.' },
          { q: '¿Los Caladinhos llegan frescos?', a: 'Sí. Los recogemos en las panaderías el mismo día o el día anterior al envío. El embalaje kraft mantiene la frescura durante el transporte.' },
          { q: '¿Cuál es la caducidad?', a: 'Los Caladinhos auténticos son mejores en los primeros 5 días, pero se conservan hasta 10 días a temperatura ambiente. La textura interior mejora ligeramente con el tiempo.' },
          { q: '¿Puedo pedir como regalo?', a: 'Sí — la Caja Regalo está especialmente diseñada para ello. Incluye 24 Caladinhos y una tarjeta con la historia de 1934. Puede indicar una dirección de entrega diferente en el checkout.' },
          { q: '¿Qué métodos de pago aceptan?', a: 'Tarjeta de crédito/débito via Stripe (Visa, Mastercard, American Express). Pago 100% seguro y encriptado.' },
          { q: '¿Envían fuera de Europa?', a: 'Enviamos a Brasil y PALOP. Para otros destinos, contáctenos via WhatsApp antes de pedir.' },
          { q: '¿Puedo cancelar o modificar mi pedido?', a: 'Puede cancelar o modificar hasta 2 horas después del pedido, contactándonos via WhatsApp. Tras la recogida en las panaderías no es posible cancelar.' },
          { q: '¿Los Caladinhos contienen gluten?', a: 'Sí — contienen harina de trigo. No son adecuados para personas con enfermedad celíaca o intolerancia al gluten.' },
        ],
      },
      cta: {
        sectionLabel: '¿Listo?',
        title: 'Prueba el dulce que',
        titleEm: 'sobrevivió a la dictadura.',
        body: 'Auténticos, artesanales, con historia. Entregados en Portugal, España, PALOP y Brasil.',
        button: 'Ver pedidos',
      },
      footer: {
        col1Title: 'Navegación',
        col1Links: [
          { label: 'La Historia', anchor: 'historia' },
          { label: 'Pedidos', anchor: 'produtos' },
          { label: 'Cómo funciona', anchor: 'processo' },
          { label: 'FAQ', anchor: 'faq' },
          { label: 'Política de Envíos', anchor: 'envios' },
        ],
        col2Title: 'Contacto',
        col2Links: [
          { label: 'ola@caladinhos.pt', href: 'mailto:ola@caladinhos.pt' },
          { label: 'Háblenos en WhatsApp', href: 'https://wa.me/351914689252' },
          { label: 'Instagram', href: 'https://instagram.com/caladinhos.pt' },
          { label: 'Facebook', href: 'https://facebook.com/caladinhos' },
        ],
        bottomTagline: 'Repostería portuguesa con historia',
      },
    },
  },
}

export const supportedLocales: Locale[] = ['pt', 'en', 'fr', 'es']
export const defaultLocale: Locale = 'pt'
